import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycCaseCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type {
  KycCaseDto,
  ReviewKycCaseRequestDto,
} from "@/features/kyc/api/dto";

/**
 * Décision finale du back-office sur un dossier KYC
 * (`POST /KycCases/{id}/review`) : APPROVED ou REJECTED. Séparation des
 * tâches : l'agent qui a collecté le dossier ne peut pas le valider ; une
 * approbation est refusée si le criblage sanctions n'est pas CLEAR.
 */
export const useReviewKycCase = (kycCaseId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "kyc-cases", "review", kycCaseId],
    mutationFn: (body: ReviewKycCaseRequestDto) =>
      mutateData({
        url: kycEndpoints.kycCases.review(kycCaseId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<KycCaseDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, kycCaseCacheKeys(kycCaseId));
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useReviewKycCase failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
